import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { InventoryItemStatus, InventoryItemType } from '@buspro/shared/common';
import {
  CompaniesApi,
  InventoryItemResponseDto,
  InventoryManagementApi,
  ProvisionResourcesDto,
} from '@buspro/web/apis';
import { ErrorDisplayComponent, ExtendedProblemDetail, useFormErrorHandler } from '@buspro/web/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-provision-resources-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzDrawerModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzButtonModule,
    NzDividerModule,
    NzTableModule,
    NzSpinModule,
    NzTabsModule,
    NzCardModule,
    NzBadgeModule,
    NzTagModule,
    NzIconModule,
    MatButtonModule,
    ErrorDisplayComponent,
    CurrencyPipe,
    DatePipe,
  ],
  template: `
    <nz-drawer
      [(nzVisible)]="visible"
      [nzTitle]="provisionResult() ? 'Invoice Preview' : 'Provision Resources'"
      [nzWidth]="provisionResult() ? '800px' : '900px'"
      [nzClosable]="true"
      (nzOnClose)="onCancel()"
      [nzBodyStyle]="{ padding: '24px' }"
    >
      <div *nzDrawerContent>
        @if (provisionResult()) {
          <!-- Invoice View -->
          <div class="invoice-view">
            <div class="mb-4">
              @if (showError()) {
                <app-error-display
                  [error]="errorDetails"
                  [showRetry]="true"
                  (retry)="onRetry()"
                  (dismiss)="onDismissError()"
                />
              }
            </div>

            <div class="mb-4">
              <button
                nz-button
                nzType="default"
                (click)="provisionResult.set(null)"
                class="mb-4"
              >
                <span class="fe fe-arrow-left mr-2"></span>
                Back to Form
              </button>
            </div>

            <div class="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
              <h4 class="text-sm font-semibold text-green-800 mb-2">✅ Provisioning Successful!</h4>
              <p class="text-sm text-green-700">
                Resources have been added to the tenant's resource pool. Payment is required before resources can be assigned.
              </p>
            </div>

            <nz-spin [nzSpinning]="invoiceQuery.isLoading() || (tenantDetailsQuery.isLoading() && !tenantDetailsQuery.data())">
              <div *ngIf="invoiceQuery.data()" class="bg-white rounded-lg border border-gray-200 p-4">
                <!-- Tenant Information Section -->
                @if (tenantDetailsQuery.isError()) {
                  <div class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p class="text-sm text-yellow-800">
                      ⚠️ Could not load tenant details. Invoice information is still available below.
                    </p>
                  </div>
                }
                @if (tenantDetailsQuery.data(); as tenantDetails) {
                  <div class="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h5 class="text-md font-semibold text-gray-800 mb-3">Tenant Information</h5>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span class="text-gray-600">Company Name:</span>
                        <span class="ml-2 font-medium text-gray-900">{{ tenantDetails.name }}</span>
                      </div>
                      @if (tenantDetails.admin) {
                        <div>
                          <span class="text-gray-600">Admin Name:</span>
                          <span class="ml-2 font-medium text-gray-900">
                            {{ tenantDetails.admin.firstName }} {{ tenantDetails.admin.surname }}
                          </span>
                        </div>
                        <div>
                          <span class="text-gray-600">Admin Email:</span>
                          <span class="ml-2 font-medium text-gray-900">{{ tenantDetails.admin.email }}</span>
                        </div>
                        <div>
                          <span class="text-gray-600">Admin Phone:</span>
                          <span class="ml-2 font-medium text-gray-900">{{ tenantDetails.admin.phoneNumber || 'N/A' }}</span>
                        </div>
                      }
                      @if (tenantDetails.address) {
                        <div class="md:col-span-2">
                          <span class="text-gray-600">Address:</span>
                          <span class="ml-2 font-medium text-gray-900">{{ tenantDetails.address }}</span>
                        </div>
                      }
                    </div>
                  </div>
                }

                <div class="mb-4">
                  <h5 class="text-lg font-semibold text-gray-800 mb-2">Invoice Details</h5>
                  <div class="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span class="text-gray-600">Invoice ID:</span>
                      <span class="ml-2 font-medium">{{ invoiceQuery.data()?.id }}</span>
                    </div>
                    <div>
                      <span class="text-gray-600">Status:</span>
                      <span class="ml-2 font-medium" [class.text-orange-600]="invoiceQuery.data()?.status === 'Pending'" 
                            [class.text-green-600]="invoiceQuery.data()?.status === 'Paid'">
                        {{ invoiceQuery.data()?.status }}
                      </span>
                    </div>
                    <div>
                      <span class="text-gray-600">Payment Method:</span>
                      <span class="ml-2 font-medium">{{ invoiceQuery.data()?.paymentMethod || 'cash' }}</span>
                    </div>
                    <div>
                      <span class="text-gray-600">Generated:</span>
                      <span class="ml-2 font-medium">{{ invoiceQuery.data()?.generatedAt | date:'short' }}</span>
                    </div>
                  </div>
                </div>

                <nz-divider></nz-divider>

                <div class="mb-4">
                  <h6 class="text-md font-semibold text-gray-700 mb-3">Line Items</h6>
                  <nz-table
                    [nzData]="invoiceQuery.data()?.lineItems || []"
                    [nzShowPagination]="false"
                    [nzSize]="'small'"
                    class="w-full"
                  >
                    <thead>
                      <tr>
                        <th>Item Name</th>
                        <th>Type</th>
                        <th class="text-right">Quantity</th>
                        <th class="text-right">Unit Price</th>
                        <th class="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let item of invoiceQuery.data()?.lineItems">
                        <td>{{ item.itemName }}</td>
                        <td>
                          <span class="badge badge-primary badge-sm">{{ item.itemType || 'N/A' }}</span>
                        </td>
                        <td class="text-right">{{ item.quantity || 1 }}</td>
                        <td class="text-right">{{ item.unitPrice || item.amount | currency: invoiceQuery.data()?.currency || 'GHS' }}</td>
                        <td class="text-right font-semibold">{{ item.totalPrice || item.amount | currency: invoiceQuery.data()?.currency || 'GHS' }}</td>
                      </tr>
                    </tbody>
                  </nz-table>
                </div>

                <div class="flex justify-end pt-4 border-t border-gray-200">
                  <div class="text-right">
                    <div class="text-sm text-gray-600 mb-1">Total Amount:</div>
                    <div class="text-2xl font-bold text-gray-800">
                      {{ invoiceQuery.data()?.totalAmount | currency: invoiceQuery.data()?.currency || 'GHS' }}
                    </div>
                  </div>
                </div>

                <div *ngIf="invoiceQuery.data()?.status === 'Pending'" class="mt-4 pt-4 border-t border-gray-200">
                  <button
                    nz-button
                    nzType="primary"
                    nzBlock
                    [nzLoading]="paymentMutation.isPending()"
                    (click)="processPayment()"
                  >
                    Process Cash Payment
                  </button>
                </div>

                <div class="mt-4 pt-4 border-t border-gray-200">
                  <button
                    nz-button
                    nzType="default"
                    nzBlock
                    (click)="onCancel()"
                  >
                    Close
                  </button>
                </div>
              </div>
            </nz-spin>
          </div>
        } @else {
          <!-- Form View -->
          <div class="form-view">
            <div class="mb-4">
              @if (showError()) {
                <app-error-display
                  [error]="errorDetails"
                  [showRetry]="true"
                  (retry)="onRetry()"
                  (dismiss)="onDismissError()"
                />
              }
            </div>

            <form [formGroup]="form" class="space-y-4">
              <nz-form-item>
                <nz-form-label nzRequired>Tenant</nz-form-label>
                <nz-form-control>
                  <nz-select 
                    formControlName="tenantId" 
                    [nzLoading]="tenantsQuery.isLoading()"
                    nzShowSearch
                    nzPlaceHolder="Search and select tenant..."
                    (ngModelChange)="onTenantChange($event)">
                    <nz-option
                      *ngFor="let tenant of tenantsQuery.data()"
                      [nzValue]="tenant.id"
                      [nzLabel]="tenant.name"
                    />
                  </nz-select>
                </nz-form-control>
              </nz-form-item>

              <nz-divider nzText="Select Resources"></nz-divider>

              <!-- Enhanced Cart Summary -->
              @if (selectedItems().length > 0) {
                <div class="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 shadow-md">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                        <span class="fe fe-shopping-cart text-white text-lg"></span>
                      </div>
                      <div>
                        <h5 class="font-bold text-gray-900 text-base">Selected Items</h5>
                        <p class="text-xs text-gray-600">{{ selectedItems().length }} item(s) in cart</p>
                      </div>
                    </div>
                    <button
                      nz-button
                      nzType="text"
                      nzDanger
                      nzSize="small"
                      (click)="selectedItems.set([])"
                      class="hover:bg-red-50"
                    >
                      <span class="fe fe-trash-2 mr-1"></span>
                      Clear All
                    </button>
                  </div>
                  <div class="flex items-center gap-4 mb-3">
                    <nz-badge [nzCount]="getCartCountByType(InventoryItemType.WIALON_DEVICE)" [nzShowZero]="false">
                      <span class="text-sm font-medium text-gray-700">Wialon</span>
                    </nz-badge>
                    <nz-badge [nzCount]="getCartCountByType(InventoryItemType.SIM_CARD)" [nzShowZero]="false">
                      <span class="text-sm font-medium text-gray-700">SIM</span>
                    </nz-badge>
                    <nz-badge [nzCount]="getCartCountByType(InventoryItemType.ANDROID_POS_DEVICE)" [nzShowZero]="false">
                      <span class="text-sm font-medium text-gray-700">Android POS</span>
                    </nz-badge>
                    <nz-badge [nzCount]="getCartCountByType(InventoryItemType.MDVR_DEVICE)" [nzShowZero]="false">
                      <span class="text-sm font-medium text-gray-700">MDVR</span>
                    </nz-badge>
                  </div>
                  <div class="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                    @for (item of selectedItems(); track item.id) {
                      <nz-tag
                        [nzColor]="getTagColor(item.itemType)"
                        class="cursor-pointer hover:opacity-80 transition-opacity"
                        (click)="removeFromCart(item.id)"
                      >
                        {{ item.name }}
                        <span class="ml-1 text-xs">×</span>
                      </nz-tag>
                    }
                  </div>
                </div>
              } @else {
                <div class="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p class="text-sm text-gray-600 text-center">
                    <span class="fe fe-info mr-2"></span>
                    Select items from the tabs below to add them to your cart
                  </p>
                </div>
              }

              <!-- Tabbed Item Selection -->
              <div class="mb-4">
                <!-- Search Bar -->
                <div class="mb-4">
                  <nz-input-group [nzSuffix]="suffixIconSearch" [nzPrefix]="prefixIconSearch">
                    <input
                      type="text"
                      nz-input
                      placeholder="Search items by name, SKU, serial number..."
                      [value]="searchQuery()"
                      (input)="onSearchChange($any($event.target).value)"
                      class="rounded-lg"
                    />
                  </nz-input-group>
                  <ng-template #prefixIconSearch>
                    <span nz-icon nzType="search" nzTheme="outline" class="text-gray-400"></span>
                  </ng-template>
                  <ng-template #suffixIconSearch>
                    @if (searchQuery()) {
                      <span
                        nz-icon
                        nzType="close-circle"
                        nzTheme="fill"
                        class="text-gray-400 cursor-pointer hover:text-gray-600"
                        (click)="clearSearch()"
                      ></span>
                    }
                  </ng-template>
                </div>

                <!-- Tabs for Item Types -->
                <nz-tabset
                  [nzSelectedIndex]="getTabIndex(selectedTab())"
                  (nzSelectedIndexChange)="onTabChange(getTabType($event))"
                  nzType="card"
                  class="modern-tabs"
                >
                  <!-- Wialon Devices Tab -->
                  <nz-tab [nzTitle]="wialonTabTitle">
                    <ng-template #wialonTabTitle>
                      <div class="flex items-center gap-2">
                        <span class="fe fe-radio text-orange-600"></span>
                        <span>Wialon Devices</span>
                        @if (getCartCountByType(InventoryItemType.WIALON_DEVICE) > 0) {
                          <nz-badge
                            [nzCount]="getCartCountByType(InventoryItemType.WIALON_DEVICE)"
                            [nzShowZero]="false"
                            class="ml-1"
                          ></nz-badge>
                        }
                      </div>
                    </ng-template>
                      <div class="mt-4">
                        @if (wialonDevicesQuery.isLoading()) {
                          <div class="flex justify-center items-center py-12">
                            <nz-spin [nzSize]="'large'"></nz-spin>
                          </div>
                        } @else if (wialonDevicesQuery.isError()) {
                          <div class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            <span class="fe fe-alert-circle mr-2"></span>
                            Error loading Wialon devices. Please try again.
                          </div>
                        } @else if (filteredWialonDevices().length === 0) {
                          <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm text-center">
                            @if (searchQuery()) {
                              <span class="fe fe-search mr-2"></span>
                              No items found matching your search.
                            } @else {
                              <span class="fe fe-inbox mr-2"></span>
                              No Wialon devices available in stock.
                            }
                          </div>
                        } @else {
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                            @for (item of filteredWialonDevices(); track item.id) {
                              <nz-card
                                [class.border-orange-400]="isInCart(item.id)"
                                [class.bg-orange-50]="isInCart(item.id)"
                                [class.shadow-md]="isInCart(item.id)"
                                class="hover:shadow-lg transition-all duration-200 cursor-pointer"
                                [nzHoverable]="true"
                              >
                                <div class="flex items-start justify-between">
                                  <div class="flex-1">
                                    <div class="flex items-center gap-2 mb-2">
                                      <h4 class="font-semibold text-gray-900 text-sm">{{ item.name }}</h4>
                                      @if (isInCart(item.id)) {
                                        <nz-tag nzColor="volcano">Selected</nz-tag>
                                      }
                                    </div>
                                    <div class="space-y-1 text-xs text-gray-600">
                                      <div><span class="font-medium">SKU:</span> {{ item.sku }}</div>
                                      @if (item.serialNumber) {
                                        <div><span class="font-medium">Serial:</span> {{ item.serialNumber }}</div>
                                      }
                                      @if (item.wialonUnitId) {
                                        <div><span class="font-medium">Wialon Unit ID:</span> {{ item.wialonUnitId }}</div>
                                      }
                                    </div>
                                  </div>
                                  <div class="ml-3">
                                    @if (isInCart(item.id)) {
                                      <button
                                        nz-button
                                        nzType="default"
                                        nzDanger
                                        nzSize="small"
                                        (click)="removeFromCart(item.id)"
                                        class="flex items-center gap-1"
                                      >
                                        <span class="fe fe-x"></span>
                                        Remove
                                      </button>
                                    } @else {
                                      <button
                                        nz-button
                                        nzType="primary"
                                        nzSize="small"
                                        (click)="addToCart(item)"
                                        class="flex items-center gap-1"
                                      >
                                        <span class="fe fe-plus"></span>
                                        Add
                                      </button>
                                    }
                                  </div>
                                </div>
                              </nz-card>
                            }
                          </div>
                        }
                      </div>
                  </nz-tab>

                  <!-- SIM Cards Tab -->
                  <nz-tab [nzTitle]="simTabTitle">
                    <ng-template #simTabTitle>
                      <div class="flex items-center gap-2">
                        <span class="fe fe-sim-card text-green-600"></span>
                        <span>SIM Cards</span>
                        @if (getCartCountByType(InventoryItemType.SIM_CARD) > 0) {
                          <nz-badge
                            [nzCount]="getCartCountByType(InventoryItemType.SIM_CARD)"
                            [nzShowZero]="false"
                            class="ml-1"
                          ></nz-badge>
                        }
                      </div>
                    </ng-template>
                      <div class="mt-4">
                        @if (simCardsQuery.isLoading()) {
                          <div class="flex justify-center items-center py-12">
                            <nz-spin [nzSize]="'large'"></nz-spin>
                          </div>
                        } @else if (simCardsQuery.isError()) {
                          <div class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            <span class="fe fe-alert-circle mr-2"></span>
                            Error loading SIM cards. Please try again.
                          </div>
                        } @else if (filteredSimCards().length === 0) {
                          <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm text-center">
                            @if (searchQuery()) {
                              <span class="fe fe-search mr-2"></span>
                              No items found matching your search.
                            } @else {
                              <span class="fe fe-inbox mr-2"></span>
                              No SIM cards available in stock.
                            }
                          </div>
                        } @else {
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                            @for (item of filteredSimCards(); track item.id) {
                              <nz-card
                                [class.border-green-400]="isInCart(item.id)"
                                [class.bg-green-50]="isInCart(item.id)"
                                [class.shadow-md]="isInCart(item.id)"
                                class="hover:shadow-lg transition-all duration-200 cursor-pointer"
                                [nzHoverable]="true"
                              >
                                <div class="flex items-start justify-between">
                                  <div class="flex-1">
                                    <div class="flex items-center gap-2 mb-2">
                                      <h4 class="font-semibold text-gray-900 text-sm">{{ item.name }}</h4>
                                      @if (isInCart(item.id)) {
                                        <nz-tag nzColor="green">Selected</nz-tag>
                                      }
                                    </div>
                                    <div class="space-y-1 text-xs text-gray-600">
                                      <div><span class="font-medium">SKU:</span> {{ item.sku }}</div>
                                      @if (item.simCardNumber) {
                                        <div><span class="font-medium">SIM Number:</span> {{ item.simCardNumber }}</div>
                                      }
                                      @if (item.simProvider) {
                                        <div><span class="font-medium">Provider:</span> {{ item.simProvider }}</div>
                                      }
                                      @if (item.simPlan) {
                                        <div><span class="font-medium">Plan:</span> {{ item.simPlan }}</div>
                                      }
                                    </div>
                                  </div>
                                  <div class="ml-3">
                                    @if (isInCart(item.id)) {
                                      <button
                                        nz-button
                                        nzType="default"
                                        nzDanger
                                        nzSize="small"
                                        (click)="removeFromCart(item.id)"
                                        class="flex items-center gap-1"
                                      >
                                        <span class="fe fe-x"></span>
                                        Remove
                                      </button>
                                    } @else {
                                      <button
                                        nz-button
                                        nzType="primary"
                                        nzSize="small"
                                        (click)="addToCart(item)"
                                        class="flex items-center gap-1"
                                      >
                                        <span class="fe fe-plus"></span>
                                        Add
                                      </button>
                                    }
                                  </div>
                                </div>
                              </nz-card>
                            }
                          </div>
                        }
                      </div>
                  </nz-tab>

                  <!-- Android POS Devices Tab -->
                  <nz-tab [nzTitle]="androidTabTitle">
                    <ng-template #androidTabTitle>
                      <div class="flex items-center gap-2">
                        <span class="fe fe-smartphone text-purple-600"></span>
                        <span>Android POS</span>
                        @if (getCartCountByType(InventoryItemType.ANDROID_POS_DEVICE) > 0) {
                          <nz-badge
                            [nzCount]="getCartCountByType(InventoryItemType.ANDROID_POS_DEVICE)"
                            [nzShowZero]="false"
                            class="ml-1"
                          ></nz-badge>
                        }
                      </div>
                    </ng-template>
                      <div class="mt-4">
                        @if (androidPosDevicesQuery.isLoading()) {
                          <div class="flex justify-center items-center py-12">
                            <nz-spin [nzSize]="'large'"></nz-spin>
                          </div>
                        } @else if (androidPosDevicesQuery.isError()) {
                          <div class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            <span class="fe fe-alert-circle mr-2"></span>
                            Error loading Android POS devices. Please try again.
                          </div>
                        } @else if (filteredAndroidPosDevices().length === 0) {
                          <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm text-center">
                            @if (searchQuery()) {
                              <span class="fe fe-search mr-2"></span>
                              No items found matching your search.
                            } @else {
                              <span class="fe fe-inbox mr-2"></span>
                              No Android POS devices available in stock.
                            }
                          </div>
                        } @else {
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                            @for (item of filteredAndroidPosDevices(); track item.id) {
                              <nz-card
                                [class.border-purple-400]="isInCart(item.id)"
                                [class.bg-purple-50]="isInCart(item.id)"
                                [class.shadow-md]="isInCart(item.id)"
                                class="hover:shadow-lg transition-all duration-200 cursor-pointer"
                                [nzHoverable]="true"
                              >
                                <div class="flex items-start justify-between">
                                  <div class="flex-1">
                                    <div class="flex items-center gap-2 mb-2">
                                      <h4 class="font-semibold text-gray-900 text-sm">{{ item.name }}</h4>
                                      @if (isInCart(item.id)) {
                                        <nz-tag nzColor="purple">Selected</nz-tag>
                                      }
                                    </div>
                                    <div class="space-y-1 text-xs text-gray-600">
                                      <div><span class="font-medium">SKU:</span> {{ item.sku }}</div>
                                      @if (item.serialNumber) {
                                        <div><span class="font-medium">Serial:</span> {{ item.serialNumber }}</div>
                                      }
                                    </div>
                                  </div>
                                  <div class="ml-3">
                                    @if (isInCart(item.id)) {
                                      <button
                                        nz-button
                                        nzType="default"
                                        nzDanger
                                        nzSize="small"
                                        (click)="removeFromCart(item.id)"
                                        class="flex items-center gap-1"
                                      >
                                        <span class="fe fe-x"></span>
                                        Remove
                                      </button>
                                    } @else {
                                      <button
                                        nz-button
                                        nzType="primary"
                                        nzSize="small"
                                        (click)="addToCart(item)"
                                        class="flex items-center gap-1"
                                      >
                                        <span class="fe fe-plus"></span>
                                        Add
                                      </button>
                                    }
                                  </div>
                                </div>
                              </nz-card>
                            }
                          </div>
                        }
                      </div>
                  </nz-tab>

                  <!-- MDVR Devices Tab -->
                  <nz-tab [nzTitle]="mdvrTabTitle">
                    <ng-template #mdvrTabTitle>
                      <div class="flex items-center gap-2">
                        <span class="fe fe-router text-blue-600"></span>
                        <span>MDVR Devices</span>
                        @if (getCartCountByType(InventoryItemType.MDVR_DEVICE) > 0) {
                          <nz-badge
                            [nzCount]="getCartCountByType(InventoryItemType.MDVR_DEVICE)"
                            [nzShowZero]="false"
                            class="ml-1"
                          ></nz-badge>
                        }
                      </div>
                    </ng-template>
                      <div class="mt-4">
                        @if (mdvrDevicesQuery.isLoading()) {
                          <div class="flex justify-center items-center py-12">
                            <nz-spin [nzSize]="'large'"></nz-spin>
                          </div>
                        } @else if (mdvrDevicesQuery.isError()) {
                          <div class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            <span class="fe fe-alert-circle mr-2"></span>
                            Error loading MDVR devices. Please try again.
                          </div>
                        } @else if (filteredMdvrDevices().length === 0) {
                          <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm text-center">
                            @if (searchQuery()) {
                              <span class="fe fe-search mr-2"></span>
                              No items found matching your search.
                            } @else {
                              <span class="fe fe-inbox mr-2"></span>
                              No MDVR devices available in stock.
                            }
                          </div>
                        } @else {
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                            @for (item of filteredMdvrDevices(); track item.id) {
                              <nz-card
                                [class.border-blue-400]="isInCart(item.id)"
                                [class.bg-blue-50]="isInCart(item.id)"
                                [class.shadow-md]="isInCart(item.id)"
                                class="hover:shadow-lg transition-all duration-200 cursor-pointer"
                                [nzHoverable]="true"
                              >
                                <div class="flex items-start justify-between">
                                  <div class="flex-1">
                                    <div class="flex items-center gap-2 mb-2">
                                      <h4 class="font-semibold text-gray-900 text-sm">{{ item.name }}</h4>
                                      @if (isInCart(item.id)) {
                                        <nz-tag nzColor="blue">Selected</nz-tag>
                                      }
                                    </div>
                                    <div class="space-y-1 text-xs text-gray-600">
                                      <div><span class="font-medium">SKU:</span> {{ item.sku }}</div>
                                      @if (item.serialNumber) {
                                        <div><span class="font-medium">Serial:</span> {{ item.serialNumber }}</div>
                                      }
                                      @if (item.mdvrDeviceIdNo) {
                                        <div><span class="font-medium">Device ID:</span> {{ item.mdvrDeviceIdNo }}</div>
                                      }
                                    </div>
                                  </div>
                                  <div class="ml-3">
                                    @if (isInCart(item.id)) {
                                      <button
                                        nz-button
                                        nzType="default"
                                        nzDanger
                                        nzSize="small"
                                        (click)="removeFromCart(item.id)"
                                        class="flex items-center gap-1"
                                      >
                                        <span class="fe fe-x"></span>
                                        Remove
                                      </button>
                                    } @else {
                                      <button
                                        nz-button
                                        nzType="primary"
                                        nzSize="small"
                                        (click)="addToCart(item)"
                                        class="flex items-center gap-1"
                                      >
                                        <span class="fe fe-plus"></span>
                                        Add
                                      </button>
                                    }
                                  </div>
                                </div>
                              </nz-card>
                            }
                          </div>
                        }
                      </div>
                  </nz-tab>
                </nz-tabset>
              </div>

              <nz-form-item>
                <nz-form-label>Notes</nz-form-label>
                <nz-form-control>
                  <textarea nz-input formControlName="notes" rows="3" placeholder="Optional notes..."></textarea>
                </nz-form-control>
              </nz-form-item>

            </form>

            <!-- Form Actions -->
            <div class="mt-6 pt-6 border-t border-gray-200 flex justify-end gap-3">
              <button nz-button nzType="default" (click)="onCancel()">Cancel</button>
              <button
                nz-button
                nzType="primary"
                [nzLoading]="provisionMutation.isPending()"
                [disabled]="form.invalid || !hasSelectedResources()"
                (click)="onSubmit()"
              >
                Provision & Generate Invoice
              </button>
            </div>
          </div>
        }
      </div>
    </nz-drawer>
  `,
})
export class ProvisionResourcesModalComponent implements OnChanges {
  @Input() visible = false;
  @Output() cancelled = new EventEmitter<void>();

  // Expose enum to template
  readonly InventoryItemType = InventoryItemType;

  private fb = inject(FormBuilder);
  private inventoryApi = inject(InventoryManagementApi);
  private companiesApi = inject(CompaniesApi);
  private notification = inject(NzNotificationService);
  private logger = inject(NGXLogger);
  private formErrorHandler = useFormErrorHandler();

  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  // Tab and search state
  selectedTab = signal<InventoryItemType>(InventoryItemType.WIALON_DEVICE);
  searchQuery = signal<string>('');

  // Computed filtered items based on selected tab and search
  filteredMdvrDevices = computed(() => {
    const items = this.mdvrDevicesQuery.data() || [];
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return items;
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.sku.toLowerCase().includes(query) ||
        item.serialNumber?.toLowerCase().includes(query) ||
        item.mdvrDeviceIdNo?.toLowerCase().includes(query),
    );
  });

  filteredSimCards = computed(() => {
    const items = this.simCardsQuery.data() || [];
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return items;
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.sku.toLowerCase().includes(query) ||
        item.simCardNumber?.toLowerCase().includes(query) ||
        item.simProvider?.toLowerCase().includes(query),
    );
  });

  filteredAndroidPosDevices = computed(() => {
    const items = this.androidPosDevicesQuery.data() || [];
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return items;
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.sku.toLowerCase().includes(query) ||
        item.serialNumber?.toLowerCase().includes(query),
    );
  });

  filteredWialonDevices = computed(() => {
    const items = this.wialonDevicesQuery.data() || [];
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return items;
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.sku.toLowerCase().includes(query) ||
        item.serialNumber?.toLowerCase().includes(query) ||
        item.wialonUnitId?.toLowerCase().includes(query),
    );
  });

  // Get current filtered items based on selected tab
  getCurrentFilteredItems(): InventoryItemResponseDto[] {
    switch (this.selectedTab()) {
      case InventoryItemType.WIALON_DEVICE:
        return this.filteredWialonDevices();
      case InventoryItemType.SIM_CARD:
        return this.filteredSimCards();
      case InventoryItemType.ANDROID_POS_DEVICE:
        return this.filteredAndroidPosDevices();
      case InventoryItemType.MDVR_DEVICE:
        return this.filteredMdvrDevices();
      default:
        return [];
    }
  }

  // Get current query based on selected tab
  getCurrentQuery() {
    switch (this.selectedTab()) {
      case InventoryItemType.WIALON_DEVICE:
        return this.wialonDevicesQuery;
      case InventoryItemType.SIM_CARD:
        return this.simCardsQuery;
      case InventoryItemType.ANDROID_POS_DEVICE:
        return this.androidPosDevicesQuery;
      case InventoryItemType.MDVR_DEVICE:
        return this.mdvrDevicesQuery;
      default:
        return this.wialonDevicesQuery;
    }
  }

  onTabChange(type: InventoryItemType) {
    this.selectedTab.set(type);
    // Clear search when switching tabs for better UX
    this.searchQuery.set('');
  }

  onSearchChange(query: string) {
    this.searchQuery.set(query);
  }

  clearSearch() {
    this.searchQuery.set('');
  }

  // Tab helper methods — order: Wialon(0), SIM(1), Android POS(2), MDVR(3)
  getTabIndex(type: InventoryItemType): number {
    switch (type) {
      case InventoryItemType.WIALON_DEVICE:
        return 0;
      case InventoryItemType.SIM_CARD:
        return 1;
      case InventoryItemType.ANDROID_POS_DEVICE:
        return 2;
      case InventoryItemType.MDVR_DEVICE:
        return 3;
      default:
        return 0;
    }
  }

  getTabType(index: number): InventoryItemType {
    switch (index) {
      case 0:
        return InventoryItemType.WIALON_DEVICE;
      case 1:
        return InventoryItemType.SIM_CARD;
      case 2:
        return InventoryItemType.ANDROID_POS_DEVICE;
      case 3:
        return InventoryItemType.MDVR_DEVICE;
      default:
        return InventoryItemType.WIALON_DEVICE;
    }
  }

  getTagColor(itemType: string): string {
    switch (itemType) {
      case InventoryItemType.WIALON_DEVICE:
        return 'volcano';
      case InventoryItemType.SIM_CARD:
        return 'green';
      case InventoryItemType.ANDROID_POS_DEVICE:
        return 'purple';
      case InventoryItemType.MDVR_DEVICE:
        return 'blue';
      default:
        return 'default';
    }
  }

  form = this.fb.group({
    tenantId: ['', Validators.required],
    provisioningType: ['RESOURCE_REQUEST', Validators.required],
    notes: [''],
  });

  provisionResult = signal<{ invoiceId: string; provisioningRecordId: string; totalAmount: number } | null>(null);
  selectedTenantId = signal<string | null>(null);
  selectedItems = signal<InventoryItemResponseDto[]>([]);

  tenantsQuery = injectQuery(() => ({
    queryKey: ['companies'],
    queryFn: () => lastValueFrom(this.companiesApi.companyControllerGetCompanies()),
  }));

  mdvrDevicesQuery = injectQuery(() => ({
    queryKey: ['inventory-items', InventoryItemType.MDVR_DEVICE, InventoryItemStatus.IN_STOCK],
    queryFn: () =>
      lastValueFrom(
        this.inventoryApi.inventoryControllerGetInventoryItems({
          itemType: InventoryItemType.MDVR_DEVICE,
          status: InventoryItemStatus.IN_STOCK,
        }),
      ),
    enabled: true, // Load when modal opens - inventory items are platform-level
  }));

  simCardsQuery = injectQuery(() => ({
    queryKey: ['inventory-items', InventoryItemType.SIM_CARD, InventoryItemStatus.IN_STOCK],
    queryFn: () =>
      lastValueFrom(
        this.inventoryApi.inventoryControllerGetInventoryItems({
          itemType: InventoryItemType.SIM_CARD,
          status: InventoryItemStatus.IN_STOCK,
        }),
      ),
    enabled: true,
  }));

  androidPosDevicesQuery = injectQuery(() => ({
    queryKey: ['inventory-items', InventoryItemType.ANDROID_POS_DEVICE, InventoryItemStatus.IN_STOCK],
    queryFn: () =>
      lastValueFrom(
        this.inventoryApi.inventoryControllerGetInventoryItems({
          itemType: InventoryItemType.ANDROID_POS_DEVICE,
          status: InventoryItemStatus.IN_STOCK,
        }),
      ),
    enabled: true,
  }));

  wialonDevicesQuery = injectQuery(() => ({
    queryKey: ['inventory-items', InventoryItemType.WIALON_DEVICE, InventoryItemStatus.IN_STOCK],
    queryFn: () =>
      lastValueFrom(
        this.inventoryApi.inventoryControllerGetInventoryItems({
          itemType: InventoryItemType.WIALON_DEVICE,
          status: InventoryItemStatus.IN_STOCK,
        }),
      ),
    enabled: true,
  }));

  provisionMutation = injectMutation(() => ({
    mutationFn: (data: ProvisionResourcesDto) =>
      lastValueFrom(
        this.inventoryApi.inventoryControllerProvisionResources({
          body: data,
        }),
      ),
    onSuccess: (result: any) => {
      this.notification.success('✅ Success', 'Resources provisioned successfully. Invoice generated.');
      this.clearErrors();
      // Set tenant ID signal for tenant details query
      const tenantId = this.form.get('tenantId')?.value;
      if (tenantId) {
        this.selectedTenantId.set(tenantId);
      }
      // Backend returns ProvisioningRecord with invoiceId and totalAmount
      this.provisionResult.set({
        invoiceId: result.invoiceId || result.invoice?.id || '',
        provisioningRecordId: result.id || '',
        totalAmount: result.totalAmount || 0,
      });
      // Fetch invoice details for preview
      if (result.invoiceId) {
        this.invoiceQuery.refetch();
      }
      // Tenant details query will automatically run when provisionResult is set and tenantId is available
      // Don't reset form or close modal yet - show invoice preview
      // User can close manually after reviewing
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to provision resources:', error);
      const result = this.formErrorHandler.handleFormError(error, {
        formName: 'provision-resources',
      });
      this.showError.set(true);
      this.errorDetails = result.problemDetail;
    },
  }));

  tenantDetailsQuery = injectQuery(() => {
    const tenantId = this.selectedTenantId();
    const provisionResult = this.provisionResult();
    const invoiceId = provisionResult?.invoiceId;
    return {
      queryKey: ['tenant-details', tenantId, invoiceId],
      queryFn: () => {
        if (!tenantId) {
          throw new Error('Tenant ID is required');
        }
        return lastValueFrom(this.companiesApi.companyControllerGetCompanyDetails({ id: tenantId }));
      },
      enabled: !!tenantId && !!provisionResult,
    };
  });

  invoiceQuery = injectQuery(() => {
    const invoiceId = this.provisionResult()?.invoiceId;
    return {
      queryKey: ['invoice', invoiceId],
      queryFn: () => {
        if (!invoiceId) {
          throw new Error('Invoice ID not available');
        }
        return lastValueFrom(this.inventoryApi.inventoryControllerGetInvoice({ invoiceId }));
      },
      enabled: !!invoiceId,
    };
  });

  paymentMutation = injectMutation(() => ({
    mutationFn: (invoiceId: string) =>
      lastValueFrom(
        this.inventoryApi.inventoryControllerProcessCashPayment({
          invoiceId,
          body: {
            paymentReference: null,
          },
        }),
      ),
    onSuccess: () => {
      this.notification.success(
        '✅ Payment Processed',
        'Cash payment processed successfully. Resources are now available for assignment.',
      );
      this.invoiceQuery.refetch();
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to process payment:', error);
      const result = this.formErrorHandler.handleFormError(error, {
        formName: 'payment-processing',
      });
      this.showError.set(true);
      this.errorDetails = result.problemDetail;
    },
  }));

  processPayment() {
    const invoiceId = this.provisionResult()?.invoiceId;
    if (invoiceId) {
      this.paymentMutation.mutate(invoiceId);
    }
  }

  // Cart management methods
  addToCart(item: InventoryItemResponseDto) {
    // Check if already in cart
    if (this.selectedItems().some((i) => i.id === item.id)) {
      this.notification.warning('Item Already Selected', `${item.name} is already in your cart`);
      return;
    }

    // Check if item is available
    if (item.status !== InventoryItemStatus.IN_STOCK) {
      this.notification.error('Item Not Available', `${item.name} is not available for provisioning`);
      return;
    }

    this.selectedItems.update((items) => [...items, item]);
    this.notification.success('✅ Added', `${item.name} added to cart`);
  }

  removeFromCart(itemId: string) {
    const item = this.selectedItems().find((i) => i.id === itemId);
    this.selectedItems.update((items) => items.filter((i) => i.id !== itemId));
    if (item) {
      this.notification.info('Removed', `${item.name} removed from cart`);
    }
  }

  isInCart(itemId: string): boolean {
    return this.selectedItems().some((i) => i.id === itemId);
  }

  getCartCountByType(type: InventoryItemType): number {
    return this.selectedItems().filter((i) => i.itemType === type).length;
  }

  getAvailableStock(type: 'mdvr' | 'sim' | 'android'): number {
    if (type === 'mdvr') {
      return this.mdvrDevicesQuery.data()?.length || 0;
    } else if (type === 'sim') {
      return this.simCardsQuery.data()?.length || 0;
    } else {
      return this.androidPosDevicesQuery.data()?.length || 0;
    }
  }

  hasSelectedResources(): boolean {
    return this.selectedItems().length > 0;
  }

  onSubmit() {
    if (this.form.valid) {
      const formValue = this.form.value;

      // Validate that at least one item is selected
      if (!this.hasSelectedResources()) {
        this.notification.warning('No Items Selected', 'Please select at least one item to provision');
        return;
      }

      const payload: ProvisionResourcesDto = {
        tenantId: formValue.tenantId!,
        provisioningType: formValue.provisioningType as any,
        inventoryItemIds: this.selectedItems().map((item) => item.id),
        notes: formValue.notes || undefined,
      };

      this.provisionMutation.mutate(payload);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // When modal opens, refetch queries to ensure fresh data
    if (changes['visible'] && this.visible) {
      this.wialonDevicesQuery.refetch();
      this.simCardsQuery.refetch();
      this.androidPosDevicesQuery.refetch();
      this.mdvrDevicesQuery.refetch();
    }
  }

  onTenantChange(tenantId: string) {
    this.selectedTenantId.set(tenantId);
    // Queries are now enabled by default, but we can refresh if needed
    // Inventory items are platform-level, so no need to refetch on tenant change
  }

  onCancel() {
    this.clearErrors();
    this.provisionResult.set(null);
    this.selectedTenantId.set(null);
    this.selectedItems.set([]);
    this.searchQuery.set('');
    this.selectedTab.set(InventoryItemType.WIALON_DEVICE);
    this.form.reset({
      tenantId: '',
      provisioningType: 'RESOURCE_REQUEST',
      notes: '',
    });
    this.cancelled.emit();
  }

  onRetry() {
    this.clearErrors();
    this.onSubmit();
  }

  onDismissError() {
    this.clearErrors();
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }
}
